import { useEffect, useRef } from "react";

import { itensSelecionaveis } from "../lib/colunas";

const ESPERA_DIGITO = 400;   // junta 2 dígitos: "12" vira comanda 12, não 1 e 2
const ESPERA_OCIOSA = 6000;  // regra do dono: a seleção se solta sozinha

/** Teclado numérico do KDS (só faz sentido com teclado físico — desktop).
 *
 *  DOIS NÍVEIS:
 *    1. sem nada selecionado, os dígitos escolhem a COMANDA pelo número do
 *       cartão;
 *    2. com a comanda escolhida, os produtos que ainda não saíram ganham um
 *       subnúmero, e cada dígito SELECIONA/desseleciona aquele produto — dá pra
 *       escolher vários ("1", "3", Enter).
 *
 *  Enter confirma: com produtos selecionados marca só eles; sem nenhum, avança
 *  a comanda inteira. É o mesmo botão do cartão — o toque também só seleciona.
 *  Esc solta a comanda (a seleção de produtos fica, visível no botão). E 6
 *  segundos parado soltam a comanda sozinhos — na cozinha ninguém volta pra
 *  desfazer o que deixou pela metade, e uma comanda escolhida esquecida faz o
 *  próximo Enter agir no pedido errado.
 */
export default function useNumpad({
  ativo, cards, selecao, setSelecao, onConfirmar, onAlternar, bufferRef, timerRef,
}) {
  const ociosoRef = useRef(null);

  useEffect(() => {
    if (!ativo) return undefined;

    const soltar = () => setSelecao({ card: -1 });
    const pararRelogio = () => { clearTimeout(ociosoRef.current); ociosoRef.current = null; };
    const reiniciarRelogio = () => {
      pararRelogio();
      ociosoRef.current = setTimeout(soltar, ESPERA_OCIOSA);
    };
    const foco = (i) => {
      const el = document.getElementById(`kds-card-${i}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const escolher = (num) => {
      if (selecao.card < 0) {
        if (num === 0) return soltar();
        if (num >= 1 && num <= cards.length) {
          setSelecao({ card: num - 1 });
          foco(num - 1);
        }
        return undefined;
      }
      // Já tem comanda: o dígito agora é o subnúmero do produto.
      const itens = itensSelecionaveis(cards[selecao.card]);
      if (num === 0) return soltar();
      if (num >= 1 && num <= itens.length) onAlternar(cards[selecao.card], itens[num - 1]);
      return undefined;
    };

    const onKey = (e) => {
      if (!cards.length) return;
      const ehDigito =
        (e.code.startsWith("Numpad") && !["NumpadEnter", "NumpadAdd", "NumpadSubtract", "NumpadDecimal"].includes(e.code))
        || (e.code >= "Digit0" && e.code <= "Digit9");

      if (ehDigito && e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        reiniciarRelogio();
        clearTimeout(timerRef.current);
        bufferRef.current += e.key;
        timerRef.current = setTimeout(() => {
          const num = parseInt(bufferRef.current, 10);
          bufferRef.current = "";
          escolher(num);
        }, ESPERA_DIGITO);
        return;
      }

      if (e.code === "NumpadEnter" || e.code === "Enter" || e.code === "NumpadAdd") {
        e.preventDefault();
        pararRelogio();
        if (selecao.card < 0 || selecao.card >= cards.length) return;
        onConfirmar(cards[selecao.card]);
        soltar();
        return;
      }

      if (e.code === "ArrowDown" || e.code === "NumpadDecimal") {
        e.preventDefault();
        reiniciarRelogio();
        const n = selecao.card < cards.length - 1 ? selecao.card + 1 : 0;
        setSelecao({ card: n });
        foco(n);
        return;
      }

      if (e.code === "ArrowUp") {
        e.preventDefault();
        reiniciarRelogio();
        const n = selecao.card > 0 ? selecao.card - 1 : cards.length - 1;
        setSelecao({ card: n });
        foco(n);
        return;
      }

      if (e.code === "Escape") {
        e.preventDefault();
        // Esc solta a comanda. A seleção de produtos fica (está no botão do
        // cartão); pra tirar um produto, o mesmo subnúmero de novo.
        pararRelogio();
        soltar();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Nada selecionado, nada pra soltar: o relógio não fica correndo à toa.
  useEffect(() => {
    if (selecao.card < 0 && ociosoRef.current) {
      clearTimeout(ociosoRef.current);
      ociosoRef.current = null;
    }
  }, [selecao.card]);

  useEffect(() => () => clearTimeout(ociosoRef.current), []);
}
